import axios from 'axios';
import {
  StockListResponse,
  StockCreateResponse,
  StockTokenResponse,
  CsvImportResponse,
  ManufacturerStock,
  BatchActivateResponse,
  StockStatus,
} from './types';

const API_URL = process.env.REACT_APP_API_URL;

interface ListFilter {
  status?: StockStatus;
  intended_company_id?: number;
  search?: string;
  take?: number;
  skip?: number;
}

// ----- Manufacturer side -----

export async function listStock(filter: ListFilter): Promise<StockListResponse> {
  const params: Record<string, unknown> = {};
  if (filter.status) params.status = filter.status;
  if (filter.intended_company_id) params.intended_company_id = filter.intended_company_id;
  if (filter.search) params.search = filter.search;
  if (filter.take !== undefined) params.take = filter.take;
  if (filter.skip !== undefined) params.skip = filter.skip;
  const { data } = await axios.get<{ data: StockListResponse }>(`${API_URL}/manufacturer/stock`, { params });
  return data.data;
}

export async function createStock(input: {
  manufacturer_id: number;
  bluetooth: string;
  cmb_number: number;
  intended_company_id?: number | null;
  notes?: string | null;
}): Promise<StockCreateResponse> {
  const { data } = await axios.post<{ data: StockCreateResponse }>(`${API_URL}/manufacturer/stock`, input);
  return data.data;
}

export async function importStockCsv(input: { manufacturer_id: number; csv: string }): Promise<CsvImportResponse> {
  const { data } = await axios.post<{ data: CsvImportResponse }>(`${API_URL}/manufacturer/stock/import-csv`, input);
  return data.data;
}

export async function updateStock(id: number, patch: {
  bluetooth?: string;
  cmb_number?: number;
  intended_company_id?: number | null;
  notes?: string | null;
}): Promise<ManufacturerStock> {
  const { data } = await axios.put<{ data: ManufacturerStock }>(`${API_URL}/manufacturer/stock/${id}`, patch);
  return data.data;
}

export async function getStockToken(id: number): Promise<StockTokenResponse> {
  const { data } = await axios.get<{ data: StockTokenResponse }>(`${API_URL}/manufacturer/stock/${id}/token`);
  return data.data;
}

export async function regenerateStockToken(id: number): Promise<StockTokenResponse> {
  const { data } = await axios.post<{ data: StockTokenResponse }>(`${API_URL}/manufacturer/stock/${id}/regen-token`);
  return data.data;
}

export function csvTemplateUrl(): string {
  return `${API_URL}/manufacturer/stock/csv-template`;
}

// ----- Admin side -----

export async function listAdminPendingStock(filter: ListFilter & { manufacturer_id?: number }): Promise<StockListResponse> {
  const params: Record<string, unknown> = {};
  if (filter.status) params.status = filter.status;
  if (filter.manufacturer_id) params.manufacturer_id = filter.manufacturer_id;
  if (filter.intended_company_id) params.intended_company_id = filter.intended_company_id;
  if (filter.search) params.search = filter.search;
  if (filter.take !== undefined) params.take = filter.take;
  if (filter.skip !== undefined) params.skip = filter.skip;
  const { data } = await axios.get<{ data: StockListResponse }>(`${API_URL}/admin/manufacturer-stock`, { params });
  return data.data;
}

// Fields collected by the activation modal — mirror of IActivateBoxPayload
// on the API. All optional client-side; API fills defaults for missing.
export interface ActivationBoxPayload {
  project_id?: number;
  name?: string;
  address?: string;
  default_password?: string;
  type?: number;
  model_type?: number;
  bluetooth_version?: number;
  lat?: number;
  long?: number;
  sim_number?: string;
  sound_sensor_sensitivity?: number;
  number_of_admissible_pulses?: number;
  maximum_time_for_open_door?: number;
  vibration_sensor_sensitivity?: number;
  relational_magnitude?: number;
  redundancy_name?: string;
  redundancy_password?: string;
}

// Lightweight project list for the activation modal's project picker.
// Endpoint already used by the projects-list page; we just need
// (id, name) pairs for the chosen company.
export async function listProjectsForCompany(company_id: number): Promise<Array<{ id: number; name: string }>> {
  // The /project/filter/:companyId endpoint requires BOTH take and skip
  // (rejects with "pagination take or skip is undefined" otherwise).
  const { data } = await axios.get<any>(
    `${API_URL}/project/filter/${company_id}`,
    { params: { take: 200, skip: 0 } },
  );
  // Response shape used elsewhere wraps the rows in { data: [...] };
  // tolerate the bare array form as a fallback for legacy callers.
  const list = Array.isArray(data) ? data : (data?.data ?? []);
  return list.map((p: any) => ({ id: Number(p.id), name: String(p.name ?? `#${p.id}`) }));
}

export async function activateStock(
  id: number,
  company_id: number,
  box?: ActivationBoxPayload,
): Promise<{ stock: ManufacturerStock; box_id: number }> {
  const { data } = await axios.post<{ data: { stock: ManufacturerStock; box_id: number } }>(
    `${API_URL}/admin/manufacturer-stock/${id}/activate`,
    { company_id, box },
  );
  return data.data;
}

export async function batchActivateStock(items: Array<{ stock_id: number; company_id: number }>): Promise<BatchActivateResponse> {
  const { data } = await axios.post<{ data: BatchActivateResponse }>(
    `${API_URL}/admin/manufacturer-stock/batch-activate`,
    { items },
  );
  return data.data;
}

export async function revokeStock(id: number, reason?: string): Promise<void> {
  await axios.post(`${API_URL}/admin/manufacturer-stock/${id}/revoke`, { reason });
}
